import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { LogIn, Mail, Lock, User, Phone, Eye, EyeOff, ArrowLeft, KeyRound, CheckCircle, MapPin, ChevronDown } from 'lucide-react';
import Navbar from '../components/Navbar';
import { trackEvent } from '../utils/analytics';

const MAJOR_CITIES = [
  "Abu Dhabi","Accra","Addis Ababa","Ahmedabad","Algiers","Amman","Amsterdam","Ankara","Atlanta","Auckland",
  "Baghdad","Bangalore","Bangkok","Barcelona","Beijing","Beirut","Belgrade","Berlin","Bogota","Boston",
  "Brussels","Bucharest","Budapest","Buenos Aires","Cairo","Calgary","Cape Town","Caracas","Casablanca","Chennai",
  "Chicago","Colombo","Copenhagen","Dallas","Dar es Salaam","Delhi","Denver","Dhaka","Doha","Dubai",
  "Dublin","Durban","Dusseldorf","Edinburgh","Frankfurt","Geneva","Glasgow","Guangzhou","Hanoi","Havana",
  "Helsinki","Ho Chi Minh City","Hong Kong","Houston","Hyderabad","Indianapolis","Istanbul","Jakarta","Jeddah","Johannesburg",
  "Karachi","Kathmandu","Kochi","Kolkata","Kozhikode","Kuala Lumpur","Kuwait City","Kyiv","Lagos","Lahore",
  "Las Vegas","Lima","Lisbon","London","Los Angeles","Lucknow","Madrid","Manchester","Manila","Marseille",
  "Mecca","Medina","Melbourne","Mexico City","Miami","Milan","Minneapolis","Montreal","Moscow","Mumbai",
  "Munich","Muscat","Nairobi","Nashville","New York","Osaka","Oslo","Ottawa","Paris","Perth",
  "Philadelphia","Phoenix","Portland","Prague","Pune","Quito","Rabat","Riyadh","Rome","San Diego",
  "San Francisco","Santiago","Sao Paulo","Seattle","Seoul","Shanghai","Shenzhen","Singapore","Stockholm","St. Petersburg",
  "Sydney","Taipei","Tehran","Tel Aviv","Thiruvananthapuram","Thrissur","Tokyo","Toronto","Trivandrum","Tunis",
  "Vancouver","Vienna","Warsaw","Washington DC","Zurich"
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialMode = searchParams.get('mode') || 'login';
  const prefillPhone = searchParams.get('phone') || '';
  const prefillName = searchParams.get('name') || '';
  const [mode, setMode] = useState(initialMode); // 'login', 'register', 'forgot'
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [countryCode, setCountryCode] = useState('+91');
  const cityDropdownRef = React.useRef(null);
  
  const [formData, setFormData] = useState({
    name: prefillName,
    identifier: '', // phone or email for login
    email: '',
    password: '',
    phone: prefillPhone,
    city: '',
    confirmPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
    setSuccessMessage('');
  };

  const filteredCities = MAJOR_CITIES.filter(c =>
    c.toLowerCase().includes(citySearch.toLowerCase())
  );

  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (cityDropdownRef.current && !cityDropdownRef.current.contains(e.target)) {
        setShowCityDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      if (mode === 'register') {
        // Validate password
        if (formData.password.length < 6) {
          setError('Password must be at least 6 characters');
          setLoading(false);
          return;
        }
        if (formData.password !== formData.confirmPassword) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }
        // Register new user - phone required, email optional
        const payload = {
          name: formData.name,
          password: formData.password,
          phone: countryCode.replace('+', '') + formData.phone.replace(/^0+/, ''),
          city: formData.city,
          email: formData.email.trim()
        };
        const response = await axios.post(`${API}/auth/user/register`, payload);
        
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify({
          id: response.data.id,
          name: response.data.name,
          email: response.data.email || '',
          phone: response.data.phone || '',
          role: response.data.role,
          email_verified: response.data.email_verified
        }));
        
        setSuccessMessage('Registration successful!');
        trackEvent('register_complete');
        setTimeout(() => navigate('/dashboard'), 1500);
      } else if (mode === 'forgot') {
        // Request password reset link via email
        await axios.post(`${API}/auth/request-reset`, {
          email: formData.email
        });
        
        setSuccessMessage('A password reset link has been sent to your email. Please check your inbox.');
        setFormData({ ...formData, email: '' });
      } else {
        // Unified login - accepts phone or email
        const response = await axios.post(`${API}/auth/login`, {
          identifier: formData.identifier,
          password: formData.password
        });
        
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify({
          id: response.data.id,
          name: response.data.name,
          email: response.data.email || '',
          phone: response.data.phone || '',
          role: response.data.role,
          email_verified: response.data.email_verified
        }));
        
        // Redirect based on role
        if (response.data.role === 'admin') {
          navigate('/admin');
        } else {
          navigate('/dashboard');
        }
      }
    } catch (error) {
      console.error('Auth error:', error);
      setError(error.response?.data?.detail || 'Operation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const validateForgotForm = () => {
    if (!formData.email) return false;
    return true;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 pt-16">
      <Navbar />
      
      <div className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-8">
            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg ${
              mode === 'forgot' ? 'bg-amber-500 shadow-amber-500/30' : 'bg-blue-600 shadow-blue-500/30'
            }`}>
              {mode === 'forgot' ? (
                <KeyRound className="w-8 h-8 text-white" />
              ) : (
                <LogIn className="w-8 h-8 text-white" />
              )}
            </div>
            <h1 className="text-3xl font-bold text-slate-900">
              {mode === 'register' ? 'Create Account' : mode === 'forgot' ? 'Reset Password' : 'Welcome Back'}
            </h1>
            <p className="text-slate-600 mt-2">
              {mode === 'register' 
                ? 'Join PropTick to list and save properties' 
                : mode === 'forgot'
                ? 'Enter your email to receive a reset link'
                : 'Login with your phone number or email'}
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                {error}
              </div>
            )}
            
            {successMessage && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl text-green-600 text-sm flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                {successMessage}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              {mode === 'register' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Full Name</label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required={mode === 'register'}
                      className="w-full h-12 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all pl-12"
                      placeholder="John Doe"
                      data-testid="input-name"
                    />
                  </div>
                </div>
              )}

              {mode === 'login' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Phone Number or Email</label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    name="identifier"
                    value={formData.identifier}
                    onChange={handleChange}
                    required
                    className="w-full h-12 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all pl-12"
                    placeholder="+91 98765 43210 or you@example.com"
                    data-testid="input-email"
                  />
                </div>
              </div>
              )}

              {mode === 'forgot' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full h-12 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all pl-12"
                    placeholder="you@example.com"
                    data-testid="input-email"
                  />
                </div>
              </div>
              )}

              {mode === 'register' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Phone Number *</label>
                <div className="flex gap-2">
                  <select
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    className="w-[110px] h-12 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all px-2 text-sm"
                    data-testid="input-country-code"
                  >
                    <option value="+91">+91 IN</option>
                    <option value="+971">+971 AE</option>
                    <option value="+1">+1 US</option>
                    <option value="+44">+44 UK</option>
                    <option value="+966">+966 SA</option>
                    <option value="+968">+968 OM</option>
                    <option value="+974">+974 QA</option>
                    <option value="+973">+973 BH</option>
                    <option value="+965">+965 KW</option>
                    <option value="+61">+61 AU</option>
                    <option value="+65">+65 SG</option>
                    <option value="+60">+60 MY</option>
                    <option value="+49">+49 DE</option>
                    <option value="+33">+33 FR</option>
                    <option value="+39">+39 IT</option>
                    <option value="+81">+81 JP</option>
                    <option value="+86">+86 CN</option>
                    <option value="+82">+82 KR</option>
                    <option value="+7">+7 RU</option>
                    <option value="+55">+55 BR</option>
                    <option value="+27">+27 ZA</option>
                    <option value="+234">+234 NG</option>
                    <option value="+254">+254 KE</option>
                    <option value="+63">+63 PH</option>
                    <option value="+92">+92 PK</option>
                    <option value="+94">+94 LK</option>
                    <option value="+880">+880 BD</option>
                    <option value="+977">+977 NP</option>
                  </select>
                  <div className="relative flex-1">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full h-12 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all pl-12"
                      placeholder="9876543210"
                      data-testid="input-phone"
                    />
                  </div>
                </div>
              </div>
              )}

              {mode === 'register' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email Address *</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full h-12 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all pl-12"
                    placeholder="you@example.com"
                    data-testid="input-email-optional"
                  />
                </div>
              </div>
              )}

              {mode !== 'forgot' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      className="w-full h-12 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all pl-12 pr-12"
                      placeholder="••••••••"
                      data-testid="input-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {mode === 'register' && (
                    <p className="text-xs text-slate-400 mt-1.5">Minimum 6 characters</p>
                  )}
                </div>
              )}

              {mode === 'register' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Confirm Password</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required
                      className="w-full h-12 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all pl-12 pr-12"
                      placeholder="••••••••"
                      data-testid="input-confirm-password"
                    />
                  </div>
                  {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                    <p className="text-xs text-red-500 mt-1.5">Passwords do not match</p>
                  )}
                </div>
              )}

              {mode === 'forgot' && (
                <p className="text-sm text-slate-500 bg-blue-50 border border-blue-100 rounded-xl p-3">
                  We'll send a secure reset link to your email address. The link expires in 30 minutes.
                </p>
              )}
              {mode === 'register' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">City</label>
                  <div className="relative" ref={cityDropdownRef}>
                    <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 z-10" />
                    <input
                      type="text"
                      value={formData.city ? formData.city : citySearch}
                      onChange={(e) => {
                        setCitySearch(e.target.value);
                        setFormData({ ...formData, city: '' });
                        setShowCityDropdown(true);
                      }}
                      onFocus={() => setShowCityDropdown(true)}
                      required
                      className="w-full h-12 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all pl-12 pr-10"
                      placeholder="Search and select city..."
                      data-testid="input-city"
                    />
                    <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    {showCityDropdown && (
                      <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl max-h-48 overflow-y-auto" data-testid="city-dropdown">
                        {filteredCities.length > 0 ? filteredCities.map((city) => (
                          <button
                            key={city}
                            type="button"
                            onClick={() => {
                              setFormData({ ...formData, city });
                              setCitySearch('');
                              setShowCityDropdown(false);
                            }}
                            className="w-full text-left px-4 py-2.5 text-sm hover:bg-blue-50 hover:text-blue-700 transition-colors flex items-center gap-2"
                            data-testid={`city-option-${city.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            <MapPin className="w-3.5 h-3.5 text-slate-400" />
                            {city}
                          </button>
                        )) : (
                          <div className="px-4 py-3 text-sm text-slate-400">No cities found</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Forgot Password Link - Only show on login mode */}
              {mode === 'login' && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setMode('forgot');
                      setError('');
                      setSuccessMessage('');
                    }}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                    data-testid="forgot-password-link"
                  >
                    Forgot Password?
                  </button>
                </div>
              )}

              {mode === 'register' && (
                <div className="flex items-start gap-2.5">
                  <input
                    type="checkbox"
                    id="agreeTerms"
                    checked={agreeTerms}
                    onChange={(e) => setAgreeTerms(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    data-testid="agree-terms-checkbox"
                  />
                  <label htmlFor="agreeTerms" className="text-sm text-slate-600 leading-snug">
                    I hereby agree to the{' '}
                    <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer" className="text-blue-600 font-medium hover:underline">Terms & Conditions</a>
                    {' '}and{' '}
                    <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-blue-600 font-medium hover:underline">Privacy Policy</a>
                  </label>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || (mode === 'forgot' && !validateForgotForm()) || (mode === 'register' && !agreeTerms)}
                className={`w-full h-12 rounded-xl text-white font-medium transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
                  mode === 'forgot' 
                    ? 'bg-amber-500 hover:bg-amber-600 shadow-amber-500/30' 
                    : 'bg-blue-600 hover:bg-blue-700 shadow-blue-500/30'
                }`}
                data-testid="submit-button"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    {mode === 'forgot' ? (
                      <>
                        <KeyRound className="w-5 h-5" />
                        Send Reset Link
                      </>
                    ) : (
                      <>
                        <LogIn className="w-5 h-5" />
                        {mode === 'register' ? 'Create Account' : 'Login'}
                      </>
                    )}
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              {mode === 'forgot' ? (
                <button
                  onClick={() => {
                    setMode('login');
                    setError('');
                    setSuccessMessage('');
                  }}
                  className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mx-auto"
                  data-testid="back-to-login"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Login
                </button>
              ) : (
                <p className="text-slate-600">
                  {mode === 'register' ? 'Already have an account?' : "Don't have an account?"}
                  <button
                    onClick={() => {
                      setMode(mode === 'register' ? 'login' : 'register');
                      setError('');
                      setSuccessMessage('');
                    }}
                    className="ml-2 text-blue-600 font-medium hover:text-blue-700"
                    data-testid="toggle-auth-mode"
                  >
                    {mode === 'register' ? 'Login' : 'Create Account'}
                  </button>
                </p>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Login;
